import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useFavorites } from "@/hooks/useFavorites";

interface FavoriteButtonProps {
  item: {
    key: string;
    type: "work" | "news";
    id: string;
    title: string;
    cover: string;
    meta: string;
  };
  variant?: "icon" | "pill";
  className?: string;
}

export default function FavoriteButton({ item, variant = "icon", className = "" }: FavoriteButtonProps) {
  const { isFavorite, toggle } = useFavorites();
  const active = isFavorite(item.key);
  const [hint, setHint] = useState("");
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const showHint = (message: string) => {
    setHint(message);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setHint(""), 2200);
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const result = toggle(item);
    if (result.full) {
      showHint("收藏已达 50 条上限");
      return;
    }
    showHint(result.added ? "已加入收藏" : "已取消收藏");
  };

  const base =
    "pointer-events-auto flex items-center justify-center transition-colors duration-200 cursor-pointer whitespace-nowrap";

  const styles =
    variant === "icon"
      ? `w-8 h-8 rounded-full backdrop-blur bg-background-50/85 ${active ? "text-accent-600" : "text-foreground-700 hover:text-accent-600"}`
      : `gap-2 px-4 h-10 rounded-md border text-sm font-medium ${
          active
            ? "bg-accent-500 border-accent-500 text-primary-950"
            : "bg-background-50 border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-600"
        }`;

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={active}
        aria-label={active ? `取消收藏 ${item.title}` : `收藏 ${item.title}`}
        className={`${base} ${styles}`}
      >
        <i className={`${active ? "ri-bookmark-fill" : "ri-bookmark-line"} text-base leading-none`} />
        {variant === "pill" && <span>{active ? "已收藏" : "收藏"}</span>}
      </button>
      {hint && (
        <span className="absolute right-0 bottom-full mb-2 z-20 px-2.5 py-1.5 rounded-md bg-foreground-900 text-background-50 text-[11px] whitespace-nowrap">
          {hint}
        </span>
      )}
    </div>
  );
}